import axiosInstance from '../axiosInstance'

// Models
import { Site, type SiteID } from '../sites/models/site'
import { Area, AreaDto, areaFromDto, AreaID } from './models/area'
import { Pagination } from '../common/models/pagination'
import { BuildingID } from '../buildings/models/building'
import { SkillID } from '../skills/models/skill'
import { EmployeeID } from '../employees/models/employee'

const AreaEndpoints = {
    base: (siteId: SiteID) => `api/orgs/${siteId}` as const,
    index: (siteId: SiteID) => `${AreaEndpoints.base(siteId)}/areas` as const,
    show: (siteId: SiteID, areaId: AreaID) => `${AreaEndpoints.index(siteId)}/${areaId}` as const,
    archive: (siteId: SiteID, areaId: AreaID) => `${AreaEndpoints.show(siteId, areaId)}/archive` as const,
    syncSkills: (siteId: SiteID, areaId: AreaID) => `${AreaEndpoints.show(siteId, areaId)}/skills/sync` as const,
    create: (siteId: SiteID, buildingId: BuildingID) => `${AreaEndpoints.base(siteId)}/buildings/${buildingId}/areas` as const,
    indexForBuilding: (siteId: SiteID, buildingId: BuildingID) => `${AreaEndpoints.base(siteId)}/buildings/${buildingId}/areas` as const,
    indexForEmployee: (siteId: SiteID, employeeId: EmployeeID) => `${AreaEndpoints.base(siteId)}/employees/${employeeId}/areas` as const,
}

export interface AreaSearchForm {
    name?: string
    archived?: boolean

    buildingId?: BuildingID

    perPage: number
    page: number
    includes?: string[]
}

interface AreaSearchParams {
    name?: string
    archived?: 0 | 1

    building_id?: BuildingID

    per_page: number
    page: number
    includes?: string[]
}

const transformSearchForm = (form: AreaSearchForm): AreaSearchParams => {
    const params: AreaSearchParams = {
        per_page: form.perPage,
        page: form.page,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.archived) {
        params.archived = form.archived ? 1 : 0
    }

    if (form.buildingId) {
        params.building_id = form.buildingId
    }

    if (form.includes) {
        params.includes = form.includes
    } else {
        params.includes = ['building']
    }

    return params
}

export const fetch = async (siteId: SiteID, form: AreaSearchForm): Promise<{ areas: Area[], pagination: Pagination }> => {
    const response = await axiosInstance.get(AreaEndpoints.index(siteId), { params: transformSearchForm(form) })

    const data = response.data.data as AreaDto[]

    return {
        areas: data.map((area) => {
            return areaFromDto(area)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const fetchByBuilding = async (siteId: SiteID, buildingId: BuildingID, form: AreaSearchForm): Promise<{ areas: Area[], pagination: Pagination }> => {
    const response = await axiosInstance.get(AreaEndpoints.indexForBuilding(siteId, buildingId), { params: transformSearchForm(form) })

    const data = response.data.data as AreaDto[]

    return {
        areas: data.map((area) => {
            return areaFromDto(area)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const fetchByEmployee = async (siteId: SiteID, employeeId: EmployeeID, form: AreaSearchForm): Promise<{ areas: Area[], pagination: Pagination }> => {
    const response = await axiosInstance.get(AreaEndpoints.indexForEmployee(siteId, employeeId), { params: transformSearchForm(form) })

    const data = response.data.data as AreaDto[]

    return {
        areas: data.map((area) => {
            return areaFromDto(area)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const show = async (siteId: SiteID, areaId: AreaID): Promise<{ area: Area }> => {
    const response = await axiosInstance.get(AreaEndpoints.show(siteId, areaId))

    const data = response.data.data as AreaDto

    return {
        area: areaFromDto(data),
    }
}

export interface AreaCreateForm {
    name: string
    description: string
    colorHex: string
    externalRef: string | null
}

interface AreaCreateData {
    name: string
    description: string
    color_hex: string
    external_ref: string | null
}

export const create = async (site: Site, departmentId: BuildingID, form: AreaCreateForm): Promise<{ area: Area }> => {
    const data: AreaCreateData = {
        name: form.name,
        description: form.description,
        color_hex: form.colorHex,
        external_ref: form.externalRef
    }

    const response = await axiosInstance.post(AreaEndpoints.create(site.id, departmentId), data)

    const areaDto = response.data.data as AreaDto

    return {
        area: areaFromDto(areaDto)
    }
}

export interface AreaUpdateForm {
    name?: string
    colorHex?: string

    addressLine1?: string
    addressLine2?: string
    city?: string
    postCode?: string
    state?: string
    country?: string
}

interface AreaUpdateData {
    name?: string
    color_hex?: string

    address_line_1?: string
    address_line_2?: string
    city?: string
    post_code?: string
    state?: string
    country?: string
}

function updateFormToData(form: AreaUpdateForm): AreaUpdateData {
    const data: AreaUpdateData = {}

    if (form.name && form.name.trim().length > 0) {
        data.name = form.name
    }

    if (form.colorHex && form.colorHex.trim().length > 0) {
        data.color_hex = form.colorHex
    }

    if (form.addressLine1) {
        data.address_line_1 = form.addressLine1
    }

    if (form.addressLine2) {
        data.address_line_2 = form.addressLine2
    }

    if (form.city) {
        data.city = form.city
    }

    if (form.postCode) {
        data.post_code = form.postCode
    }

    if (form.state) {
        data.state = form.state
    }

    if (form.country) {
        data.country = form.country
    }

    return data
}

export const update = async (siteId: SiteID, areaId: AreaID, form: AreaUpdateForm): Promise<{ area: Area }> => {
    const data = updateFormToData(form)

    const response = await axiosInstance.patch(AreaEndpoints.show(siteId, areaId), data)

    const areaDto = response.data.data as AreaDto

    return {
        area: areaFromDto(areaDto)
    }
}

export const archive = async (site: Site, areaId: AreaID): Promise<void> => {
    await axiosInstance.put(AreaEndpoints.archive(site.id, areaId))
}

export const restore = async (site: Site, areaId: AreaID): Promise<void> => {
    await axiosInstance.delete(AreaEndpoints.archive(site.id, areaId))
}

export interface AreaSkillSyncForm {
    skillIds: SkillID[]
}

interface AreaSkillSyncData {
    skill_ids: SkillID[]
}

function syncFormToData(form: AreaSkillSyncForm): AreaSkillSyncData {
    const data: AreaSkillSyncData = {
        skill_ids: form.skillIds
    }

    return data
}

export const syncSkills = async (siteId: SiteID, areaId: AreaID, form: AreaSkillSyncForm): Promise<{ area: Area }> => {
    const data = syncFormToData(form)

    const response = await axiosInstance.post(AreaEndpoints.syncSkills(siteId, areaId), data)

    const areaDto = response.data.data as AreaDto

    return {
        area: areaFromDto(areaDto)
    }
}